import React, { useState, useRef } from "react"

import { Modal } from "@tenforce/toolbox-modal-react"
import { Action, ActionType } from "@tenforce/toolbox-button-push-react"
import { Symbols } from "@tenforce/toolbox-fontmap"

import { GlobalState } from "../../../../store"
import { useAppSelector } from "../../../../hooks"

import Separator from "../../../common/Separator"

import { IRCAFinding, IRootCauseAnalysisActions } from "../.."

export interface Props {
    finding: IRCAFinding
    actions: IRootCauseAnalysisActions
}

export default function FindingContextMenu({
    finding,
    actions,
}: Props): JSX.Element {
    const targetRef = useRef<HTMLButtonElement>(null)
    const [isVisible, setIsVisible] = useState(false)
    const { labels } = useAppSelector((state: GlobalState) => state.incidents)
    const {
        canCreateNewFiveWhys,
        canCreateNewIshikawa,
        canCreateNewParent,
        isClearable,
        isDeletable,
        isMarkable,
        isMarkedAsRootCause,
        canAddNewAction,
        canLinkExistingAction,
    } = finding

    const isMarkButtonShown = isMarkable || isMarkedAsRootCause

    // SECTIONS
    const hasFirstSection = isMarkButtonShown || canCreateNewParent
    const hasSecondSection =
        canCreateNewFiveWhys ||
        canCreateNewIshikawa ||
        canAddNewAction ||
        canLinkExistingAction
    const hasThirdSection = isClearable
    const hasFourthSection = isDeletable

    // CONTEXT MENU VISIBILITY
    // if no actions are allowed then we should not show the context menu either
    if (
        !hasFirstSection &&
        !hasSecondSection &&
        !hasThirdSection &&
        !hasFourthSection
    ) {
        return <></>
    }

    // SEPARATORS
    // only show the separators if there are at least 2 sections visible
    const isFirstSeparatorShown = hasFirstSection && hasSecondSection

    const isSecondSeparatorShown =
        (hasFirstSection || hasSecondSection) && hasThirdSection

    const isThirdSeparatorShown =
        (hasFirstSection || hasSecondSection || hasThirdSection) &&
        hasFourthSection

    const separator = <Separator />

    // MARK BUTTON CONTENT
    const markButtonText = isMarkedAsRootCause
        ? labels.unmarkAsRootCause
        : labels.markAsRootCause

    const markButtonIcon = isMarkedAsRootCause
        ? Symbols.Cancel
        : Symbols.Confirm

    const buttonAlignmentClasses = "tw-px-4 tw-py-2"

    const hideModal = (): void => {
        setIsVisible(false)
    }

    return (
        <>
            <Action
                data-ft-component="showFindingContextMenu"
                actionType={ActionType.icon}
                onClick={(): void => {
                    setIsVisible(true)
                }}
                ref={targetRef}
                extraClasses="is-hidden-action"
                title={labels.contextMenu}
            >
                {Symbols.EllipsisVertical}
            </Action>
            <Modal
                isShown={isVisible}
                containerProps={{
                    targetRef,
                    id: "rcaFindingContextMenu",
                    onClose: hideModal,
                }}
                innerContentProps={{
                    extraClasses: "bg-white",
                }}
            >
                <div className="tw-flex tw-flex-col tw-py-2 tw-gap-2">
                    {isMarkButtonShown && (
                        <Action
                            actionType={ActionType.block}
                            extraClasses={buttonAlignmentClasses}
                            title={markButtonText}
                            data-ft-component="markAsRootCause"
                            data-ft-is-marked-as-root-cause={
                                isMarkedAsRootCause
                            }
                            onClick={(): void => {
                                actions.setIsMarkedAsRootCause(
                                    finding,
                                    !finding.isMarkedAsRootCause,
                                )
                                hideModal()
                            }}
                        >
                            {markButtonIcon} {markButtonText}
                        </Action>
                    )}

                    {canCreateNewParent && (
                        <Action
                            actionType={ActionType.block}
                            extraClasses={buttonAlignmentClasses}
                            title={labels.addParentFinding}
                            data-ft-component="addParentFinding"
                            onClick={(): void => {
                                actions.addParentFinding(finding)
                                hideModal()
                            }}
                        >
                            {Symbols.Create} {labels.addParentFinding}
                        </Action>
                    )}

                    {isFirstSeparatorShown && separator}

                    {canCreateNewFiveWhys && (
                        <Action
                            actionType={ActionType.block}
                            extraClasses={buttonAlignmentClasses}
                            title={labels.addFiveWhysFinding}
                            data-ft-component="addFiveWhys"
                            onClick={(): void => {
                                actions.addFiveWhys(finding.id)
                                hideModal()
                            }}
                        >
                            {Symbols.Create} {labels.addFiveWhysFinding}
                        </Action>
                    )}

                    {canCreateNewIshikawa && (
                        <Action
                            actionType={ActionType.block}
                            extraClasses={buttonAlignmentClasses}
                            title={labels.addIshikawaFinding}
                            data-ft-component="addIshikawa"
                            onClick={(): void => {
                                actions.addIshikawa(finding.id)
                                hideModal()
                            }}
                        >
                            {Symbols.Create} {labels.addIshikawaFinding}
                        </Action>
                    )}

                    {canAddNewAction && (
                        <Action
                            actionType={ActionType.block}
                            extraClasses={buttonAlignmentClasses}
                            title={labels.addNewAction}
                            data-ft-component="addNewAction"
                            onClick={(): void => {
                                actions.createNewAction(finding)
                                hideModal()
                            }}
                        >
                            {Symbols.Create} {labels.addNewAction}
                        </Action>
                    )}

                    {canLinkExistingAction && (
                        <Action
                            actionType={ActionType.block}
                            extraClasses={buttonAlignmentClasses}
                            title={labels.linkExistingAction}
                            data-ft-component="linkExistingAction"
                            onClick={(): void => {
                                actions.linkAnAction(finding)
                                hideModal()
                            }}
                        >
                            {Symbols.Link} {labels.linkExistingAction}
                        </Action>
                    )}

                    {isSecondSeparatorShown && separator}

                    {isClearable && (
                        <Action
                            actionType={ActionType.block}
                            extraClasses={buttonAlignmentClasses}
                            title={labels.clear}
                            data-ft-component="clear"
                            onClick={(): void => {
                                actions.clear(finding)
                                hideModal()
                            }}
                        >
                            {Symbols.Delete} {labels.clear}
                        </Action>
                    )}

                    {isThirdSeparatorShown && separator}

                    {isDeletable && (
                        <Action
                            actionType={ActionType.block}
                            extraClasses={buttonAlignmentClasses}
                            title={labels.delete}
                            data-ft-component="delete"
                            onClick={(): void => {
                                actions.delete(finding)
                                hideModal()
                            }}
                        >
                            {Symbols.Delete} {labels.delete}
                        </Action>
                    )}
                </div>
            </Modal>
        </>
    )
}
